import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Btn } from "@/components/Btn";
import { useEventMapping, useSaveEventMapping } from "@/lib/api-hooks";
import type { EventMappingEntry } from "@/lib/types";
import { useOrgPath } from "@/lib/useOrgNav";
import { SettingsSection } from "./_legacy_section";

/**
 * /settings/integrations/:id/event-mapping — the gate that unlocks
 * derived metrics (ROAS / CVR / revenue) across the app. The admin
 * reviews each native event type the ingester has seen and picks its
 * canonical bucket (trial.started, subscription.renewed, …). Saving
 * flips `org_integrations.mapping_confirmed_at` and every stats query
 * re-fetches with the gate open.
 *
 * The page lists one row per native event type with a dropdown. We
 * show the auto-suggested canonical default so the admin usually just
 * has to scan, confirm, and Save.
 */
export function EventMappingPage() {
  const { integrationId: raw } = useParams<{ integrationId: string }>();
  const integrationId = Number(raw);
  const orgPath = useOrgPath();

  const query = useEventMapping(integrationId);
  const saveMut = useSaveEventMapping();

  // Local draft for in-flight edits; synced from server data when it
  // arrives / refetches.
  const [draft, setDraft] = useState<Map<string, string | null>>(new Map());

  useEffect(() => {
    if (!query.data) return;
    const m = new Map<string, string | null>();
    for (const e of query.data.entries) {
      m.set(e.native_event_type, e.canonical_code);
    }
    setDraft(m);
  }, [query.data]);

  if (query.isLoading) {
    return (
      <SettingsSection title="Event mapping">
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Loading mapping…
        </div>
      </SettingsSection>
    );
  }
  if (query.error || !query.data) {
    return (
      <SettingsSection title="Event mapping">
        <div className="card card-pad" style={{ textAlign: "center" }}>
          Couldn&apos;t load mapping.
        </div>
      </SettingsSection>
    );
  }

  const data = query.data;
  const unconfirmed = data.entries.filter((e) => !e.is_user_confirmed).length;
  const confirmed = !!data.mapping_confirmed_at;

  const save = async (confirm: boolean) => {
    const entries = data.entries.map((e) => ({
      native_event_type: e.native_event_type,
      canonical_code: draft.get(e.native_event_type) ?? null,
    }));
    await saveMut.mutateAsync({ integration_id: integrationId, entries, confirm });
  };

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Settings", to: orgPath("/settings/integrations") },
          { label: "Integrations", to: orgPath("/settings/integrations") },
          { label: `${data.provider_code}: event mapping` },
        ]}
      />

      <SettingsSection
        title="Event mapping"
        description={`Review how we classify each event type that ${data.provider_code} sends. Saving confirms the mapping and unlocks revenue / ROAS / CVR across the dashboard.`}
        actions={
          <>
            <Btn
              onClick={() => void save(false)}
              disabled={saveMut.isPending}
            >
              Save draft
            </Btn>
            <Btn
              variant="primary"
              onClick={() => void save(true)}
              disabled={saveMut.isPending}
            >
              {confirmed ? "Save & re-confirm" : "Save & confirm"}
            </Btn>
          </>
        }
      >
        <ConfirmationBanner
          confirmed={confirmed}
          confirmedAt={data.mapping_confirmed_at}
          unconfirmed={unconfirmed}
        />

        {data.entries.length === 0 && (
          <div className="card card-pad muted" style={{ textAlign: "center" }}>
            No events seen from this provider yet. Come back after the first webhook arrives.
          </div>
        )}

        {data.entries.length > 0 && (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <thead>
                  <tr>
                    <th>Native event</th>
                    <th>First seen</th>
                    <th style={{ minWidth: 260 }}>Canonical bucket</th>
                    <th>Confirmed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <MappingRow
                      key={e.native_event_type}
                      entry={e}
                      value={draft.get(e.native_event_type) ?? null}
                      onChange={(v) =>
                        setDraft((prev) => {
                          const next = new Map(prev);
                          next.set(e.native_event_type, v);
                          return next;
                        })
                      }
                      vocabulary={data.canonical_vocabulary}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="muted" style={{ fontSize: 12, marginTop: 14 }}>
          <Link to={orgPath("/settings/integrations")}>← Back to integrations</Link>
        </p>
      </SettingsSection>
    </div>
  );
}

function ConfirmationBanner({
  confirmed,
  confirmedAt,
  unconfirmed,
}: {
  confirmed: boolean;
  confirmedAt: string | null;
  unconfirmed: number;
}) {
  if (!confirmed) {
    return (
      <div
        className="card"
        style={{
          padding: "12px 16px",
          marginBottom: 16,
          background: "var(--c-warn-50)",
          borderColor: "transparent",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--c-warn-500)" }}>
          Not confirmed yet
        </div>
        <div style={{ fontSize: 12.5, color: "var(--c-text-2)", marginTop: 2 }}>
          Revenue and ROAS stay hidden in the dashboard until this mapping is
          reviewed and saved. Ingester has auto-suggested defaults — you can
          accept them all by clicking Save & confirm.
        </div>
      </div>
    );
  }
  return (
    <div
      className="card"
      style={{
        padding: "12px 16px",
        marginBottom: 16,
        background: "var(--c-pos-50)",
        borderColor: "transparent",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--c-pos-600)" }}>
        Confirmed {confirmedAt ? `· ${new Date(confirmedAt).toLocaleString()}` : ""}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--c-text-2)", marginTop: 2 }}>
        {unconfirmed > 0
          ? `${unconfirmed} new event type${unconfirmed === 1 ? "" : "s"} have been auto-seeded since your last confirmation. Review and re-confirm to include them in metrics.`
          : "All events are classified and counted."}
      </div>
    </div>
  );
}

function MappingRow({
  entry,
  value,
  onChange,
  vocabulary,
}: {
  entry: EventMappingEntry;
  value: string | null;
  onChange: (v: string | null) => void;
  vocabulary: { code: string; display_name: string; category: string }[];
}) {
  return (
    <tr>
      <td className="mono" style={{ fontSize: 12.5 }}>
        {entry.native_event_type}
      </td>
      <td className="muted">{new Date(entry.first_seen_at).toLocaleDateString()}</td>
      <td>
        <select
          className="select"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          style={{ minWidth: 240 }}
        >
          <option value="">— Do not count —</option>
          {Object.entries(groupByCategory(vocabulary)).map(([category, items]) => (
            <optgroup key={category} label={category}>
              {items.map((v) => (
                <option key={v.code} value={v.code}>
                  {v.display_name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </td>
      <td>
        {entry.is_user_confirmed ? (
          <span className="badge enabled">
            <span className="dot" /> Confirmed
          </span>
        ) : (
          <span className="badge warn">Auto-suggested</span>
        )}
      </td>
    </tr>
  );
}

function groupByCategory<T extends { category: string }>(items: T[]): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    (groups[item.category] ??= []).push(item);
  }
  return groups;
}
